import Link from 'next/link'
import type { RichTextItemResponse } from '@notionhq/client/build/src/api-endpoints'
import * as styles from './notionText.css'

/**
 * Google Drive share links are pasted as links but meant as images; this is the
 * documented way to turn one into a direct image URL.
 * @see https://stackoverflow.com/a/20675890/8464260
 */
function driveImageUrl(href: string) {
  const id = href.split('/').at(-2)
  return id ? `https://drive.google.com/uc?id=${id}` : undefined
}

export default function NotionText({
  content,
}: {
  content: RichTextItemResponse[] | string | undefined | null
}) {
  if (!content) return null
  if (typeof content === 'string') return content

  return content.map((value, index) => {
    if (value.href?.startsWith('https://drive.google.com')) {
      const url = driveImageUrl(value.href)
      if (!url) return null
      // oxlint-disable-next-line no-img-element
      return <img key={value.href} src={url} alt='' className={styles.image} />
    }

    const { bold, code, color, italic, strikethrough, underline } =
      value.annotations
    const className = [
      bold ? styles.bold : '',
      code ? styles.code : '',
      italic ? styles.italic : '',
      strikethrough ? styles.strikethrough : '',
      underline ? styles.underline : '',
      color.startsWith('gray') ? styles.gray : '',
    ]
      .filter(Boolean)
      .join(' ')
    // `gray` is themed via a class; other Notion colours pass through literally.
    const style =
      color !== 'default' && !color.startsWith('gray') ? { color } : undefined
    const link = value.type === 'text' ? value.text.link : null

    // Rich text has no stable id, and it is re-derived whole on every read.
    return (
      <span key={index} className={className} style={style}>
        {link ? (
          <Link href={link.url}>{value.plain_text}</Link>
        ) : (
          value.plain_text
        )}
      </span>
    )
  })
}
